import { useContext, useEffect, useState } from 'react';
import { Card, Divider, Skeleton, Tag, Tooltip } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import { IDashboardTopCompanyItem } from '../../../../services/http/mappers/DashboardMapper';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import { formatCnpj } from '../../../../utils/Masks';
import { poeValorComPontosEmMil } from '../../../../utils/Conversores';

interface TopCompaniesListProps {
    data: IDashboardTopCompanyItem[];
    hasCitySelected: boolean;
    isLoading: boolean;
    typeSelected: string;
    onTypeChange: (type: string) => void;
}

const TYPE_NOTA: { key: string; label: string }[] = [
    { key: 'todas', label: 'Todas' },
    { key: 'NFC', label: 'NFC' },
    { key: 'NFE', label: 'NFE' },
    { key: 'NFS', label: 'NFS' },
];

const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];

const MiniStat = ({
    label,
    value,
    color,
    width = 72,
}: {
    label: string;
    value: string | number;
    color: string;
    width?: number;
}) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: width,
            minWidth: width,
            flexShrink: 0,
            textAlign: "center",
        }}
    >
        <div
            style={{
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
            }}
        >
            {label}
        </div>

        <div
            style={{
                marginTop: 3,
                fontSize: 15,
                fontWeight: 700,
                color: color,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: width,
            }}
        >
            {typeof value === 'number' ? poeValorComPontosEmMil(value) : value}
        </div>
    </div>
);

const TopCompaniesSkeleton = ({ isDark }: { isDark: boolean }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[1, 2, 3, 4, 5].map(key => (
            <div
                key={key}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 72,
                    borderRadius: 12,
                    background: isDark ? '#141414' : '#fafafa',
                    border: `1px solid ${isDark ? '#2a2a2a' : '#ececec'}`,
                    padding: '12px 20px',
                    gap: 16,
                    overflow: 'hidden',
                }}
            >
                <Skeleton.Avatar active size={44} shape="circle" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Skeleton.Input active size="small" style={{ width: '60%', height: 16, borderRadius: 4 }} />
                    <Skeleton.Input active size="small" style={{ width: '40%', height: 12, borderRadius: 4 }} />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <Skeleton.Button active size="small" style={{ width: 72, height: 26, borderRadius: 4 }} />
                    <Skeleton.Button active size="small" style={{ width: 72, height: 26, borderRadius: 4 }} />
                </div>
                <Skeleton.Button active size="large" style={{ width: 135, height: 48, borderRadius: 8, flexShrink: 0 }} />
            </div>
        ))}
    </div>
);

export default function TopCompaniesList({ data, isLoading, hasCitySelected, typeSelected, onTypeChange }: TopCompaniesListProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    return (
        <Card
            title={
                <div style={{ paddingTop: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 20 }}>Top Empresas Emissoras</div>
                    <div style={{ fontWeight: 400, fontSize: 18, color: '#888' }}>Ranking por total de notas importadas</div>
                </div>
            }
            extra={
                <div
                    style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                        justifyContent: isMobile ? "flex-start" : "flex-end",
                    }}
                >
                    {TYPE_NOTA.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => onTypeChange(key)}
                            style={{
                                padding: '4px 12px',
                                borderRadius: 6,
                                border: '1px solid',
                                borderColor: typeSelected === key ? '#1677ff' : (theme === 'light' ? '#d9d9d9' : '#434343'),
                                background: typeSelected === key ? '#1677ff' : (theme === 'light' ? '#fff' : '#1f1f1f'),
                                color: typeSelected === key ? '#fff' : (theme === 'light' ? '#555' : '#aaa'),
                                fontSize: 14,
                                fontWeight: typeSelected === key ? 600 : 400,
                                cursor: 'pointer',
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            }
            style={{
                background: isDark ? '#1f1f1f' : '#FFFFFF',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: 'none',
                flex: 1,
            }}
            bodyStyle={{ padding: '8px 20px 20px' }}
        >
            {isLoading ? (
                <TopCompaniesSkeleton isDark={isDark} />
            ) : data.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: '#888' }}>
                    Nenhum dado disponível
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 480, overflowY: 'auto', paddingRight: 4 }}>
                    {data.map((item, index) => {
                        const rankColor = rankColors[index] ?? (isDark ? '#444' : '#e8e8e8');
                        const isTopThree = index < 3;

                        return (
                            <div
                                key={item.cpfCnpj}
                                style={{
                                    display: 'flex',
                                    flexDirection: isMobile ? "column" : "row",
                                    alignItems: "stretch",
                                    minHeight: isMobile ? 'auto' : 72,
                                    borderRadius: 12,
                                    background: isDark ? '#141414' : '#fafafa',
                                    border: `1px solid ${isDark ? '#2a2a2a' : '#ececec'}`,
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1,
                                        minWidth: 0,
                                        flexDirection: isMobile ? "column" : "row",
                                        alignItems: "center",
                                        gap: 16,
                                        padding: isMobile ? '14px 16px' : '12px 20px',
                                    }}
                                >
                                    {!isMobile && (
                                        <div
                                            style={{
                                                width: 44,
                                                height: 44,
                                                minWidth: 44,
                                                borderRadius: '50%',
                                                background: isTopThree ? rankColor : (isDark ? '#2a2a2a' : '#e8e8e8'),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 700,
                                                fontSize: isTopThree ? 22 : 18,
                                                color: isTopThree ? (index === 0 ? '#7a5800' : '#555') : (isDark ? '#aaa' : '#666'),
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isTopThree ? <TrophyOutlined /> : index + 1}
                                        </div>
                                    )}

                                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Tooltip title={item.company}>
                                            <div
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: isMobile ? 15 : 17,
                                                    lineHeight: 1.3,
                                                    color: isDark ? "#e8e8e8" : "#222",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {item.company}

                                                {!hasCitySelected && item.city && (
                                                    <Tag
                                                        color="success"
                                                        style={{
                                                            marginLeft: 10,
                                                            borderRadius: 6,
                                                            flexShrink: 0,
                                                            fontSize: 12,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {item.city}
                                                    </Tag>
                                                )}
                                            </div>
                                        </Tooltip>

                                        <div
                                            style={{
                                                fontSize: 13,
                                                lineHeight: 1.3,
                                                color: "#888",
                                                marginTop: 2,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {formatCnpj(item.cpfCnpj)}
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: 4,
                                            flexShrink: 0,
                                            alignItems: "center",
                                        }}
                                    >
                                        <MiniStat
                                            label="Válidas"
                                            value={item.totalValid}
                                            color="#389e0d"
                                            width={72}
                                        />

                                        <Divider
                                            type="vertical"
                                            style={{
                                                height: 36,
                                                margin: "0 4px",
                                            }}
                                        />

                                        <MiniStat
                                            label="Erros"
                                            value={item.totalError}
                                            color="#cf1322"
                                            width={72}
                                        />
                                    </div>
                                </div>

                                <div
                                    style={{
                                        alignSelf: 'stretch',
                                        background: isDark ? '#1a1a1a' : '#f5f5f5',
                                        borderLeft: isMobile ? 'none' : `1px solid ${isDark ? '#2a2a2a' : '#ececec'}`,
                                        borderTop: isMobile ? `1px solid ${isDark ? '#2a2a2a' : '#ececec'}` : 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: isMobile ? '12px 16px' : '0 16px',
                                        width: isMobile ? 'auto' : 135,
                                        minWidth: isMobile ? 'auto' : 135,
                                        maxWidth: isMobile ? 'auto' : 135,
                                        flexShrink: 0,
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 600,
                                            lineHeight: 1,
                                            color: '#888',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.4px',
                                            whiteSpace: 'nowrap',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Total de Notas
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 4,
                                            fontSize: 20,
                                            fontWeight: 700,
                                            color: isDark ? '#e8e8e8' : '#1677ff',
                                            lineHeight: 1.1,
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {poeValorComPontosEmMil(item.total)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}
